import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo, { localBusinessLd, breadcrumbLd } from "@/lib/seo";

const METIERS = [
  { n: "Peinture & préparation", d: "Protection, préparation des supports, rebouchage, enduits, ponçage, impression et finitions peinture (murs, plafonds, portes, boiseries, canalisations, PVC).", img: "https://images.unsplash.com/photo-1503174971373-b1f69850bded?w=1200&q=85" },
  { n: "Placo, cloisons & plafonds", d: "Cloisons non porteuses, doublages intérieurs, faux plafonds, bandes et enduits. Isolation acoustique et thermique intérieure.", img: "https://images.unsplash.com/photo-1503174971373-b1f69850bded?w=1200&q=85" },
  { n: "Sols, parquet & carrelage", d: "Parquet stratifié ou contrecollé, sols souples, carrelage sol/mur, faïence, ragréages et plinthes.", img: "https://images.unsplash.com/photo-1639663742190-1b3dba2eebcf?w=1200&q=85" },
  { n: "Électricité intérieure", d: "Prises, interrupteurs, points lumineux, spots encastrés, tableaux, mise aux normes intérieure.", img: "https://images.unsplash.com/photo-1639751787355-bbc3ed1fd639?w=1200&q=85" },
  { n: "Plomberie intérieure", d: "Alimentation, évacuation, sanitaires, robinetterie, déplacement de points d'eau à l'intérieur du bâtiment.", img: "https://images.unsplash.com/photo-1756079664354-34944e001f6d?w=1200&q=85" },
  { n: "Salle de bains", d: "Rénovation intégrale : dépose, réseaux, carrelage, faïence, sanitaires et robinetterie.", img: "https://images.unsplash.com/photo-1564540579594-0930edb6de43?w=1200&q=85" },
  { n: "Cuisine & agencement", d: "Réseaux, sols, crédence, faïence et coordination des travaux intérieurs autour de la cuisine.", img: "https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c?w=1200&q=85" },
  { n: "Menuiserie intérieure", d: "Portes intérieures, bâtis, plinthes, éléments bois et huisseries.", img: "https://images.unsplash.com/photo-1639751787355-bbc3ed1fd639?w=1200&q=85" },
  { n: "Isolation intérieure", d: "Doublages isolants thermiques et phoniques, non structurels.", img: "https://images.unsplash.com/photo-1680416124510-5eae1beca412?w=1200&q=85" },
  { n: "Dépose non structurelle", d: "Dépose d'éléments non porteurs : revêtements, sanitaires anciens, cloisons légères. Aucune intervention sur la structure.", img: "https://images.unsplash.com/photo-1741764014072-68953e93cd48?w=1200&q=85" },
  { n: "Finitions", d: "Joints acryliques, reprises, bandes et petites remises en état pour un rendu impeccable.", img: "https://images.unsplash.com/photo-1503174971373-b1f69850bded?w=1200&q=85" },
  { n: "Rénovation intérieure complète", d: "Coordination de tous les lots intérieurs, planning, suivi et livraison.", img: "https://images.unsplash.com/photo-1741764014072-68953e93cd48?w=1200&q=85" },
  { n: "Lots intérieurs de logements neufs", d: "Second œuvre en bâtiment neuf : placo, enduits, peintures, sols, portes et finitions.", img: "https://images.unsplash.com/photo-1680416124510-5eae1beca412?w=1200&q=85" },
];

export default function MetiersPage() {
  const [hover, setHover] = useState(0);
  return (
    <div className="pt-[100px] md:pt-[140px] pb-24">
      <Seo
        title="Nos métiers — Travaux intérieurs, rénovation et second œuvre | RAMA DÉCO"
        description="Peinture, placo et cloisons, sols et carrelage, électricité et plomberie intérieures, salle de bains, cuisine, menuiserie et isolation intérieure : tous les métiers RAMA DÉCO."
        path="/nos-metiers"
        jsonLd={[
          localBusinessLd(),
          breadcrumbLd([
            { to: "/", label: "Accueil" },
            { to: "/nos-metiers", label: "Nos métiers" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Métiers RAMA DÉCO",
            itemListElement: METIERS.map((m, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: m.n,
              description: m.d,
            })),
          },
        ]}
      />
      <div className="wrap">
        <div className="font-mono-caption text-bordeaux-700 mb-4">Nos métiers intérieurs</div>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight text-anthracite max-w-3xl">Tout ce que RAMA DÉCO peut prendre en charge à l'<span className="italic text-bordeaux-700">intérieur</span> de vos espaces.</h1>
        <p className="mt-6 max-w-2xl text-ink-soft text-lg leading-relaxed">Rénovation d'espaces existants ou second œuvre en logement neuf : nos métiers couvrent l'intégralité des travaux intérieurs. RAMA DÉCO n'intervient pas sur le gros œuvre ni sur la structure du bâtiment.</p>

        <div className="mt-16 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <ul>
              {METIERS.map((m, i) => (
                <li
                  key={m.n}
                  data-testid={`metier-row-${i}`}
                  onMouseEnter={() => setHover(i)}
                  className={`border-b border-black/10 py-6 md:py-8 cursor-pointer transition-colors ${hover === i ? "text-anthracite" : "text-ink-soft hover:text-anthracite"}`}
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <div className="flex items-baseline gap-6">
                      <span className="font-mono-caption text-ink-soft">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-display text-2xl md:text-4xl tracking-tight">{m.n}</span>
                    </div>
                    <ArrowRight size={22} className={hover === i ? "text-bordeaux-700" : "text-ink-soft/40"} />
                  </div>
                  {hover === i && (
                    <div className="mt-3 pl-14 text-sm md:text-base text-ink-soft max-w-2xl">{m.d}</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <div className="sticky top-32 aspect-[4/5] overflow-hidden rounded-rama-md">
              <img key={METIERS[hover].img} src={METIERS[hover].img} alt={METIERS[hover].n} className="w-full h-full object-cover animate-fade-up" />
            </div>
          </div>
        </div>

        <div className="mt-20 flex flex-col md:flex-row gap-4">
          <Link to="/estimation" className="btn-primary">Estimer un projet <ArrowRight size={16} /></Link>
          <Link to="/contact" className="btn-ghost">Nous contacter</Link>
        </div>
      </div>
    </div>
  );
}
